import { css } from '@emotion/core';
import { Theme } from '@material-ui/core';
import * as React from 'react';

import { ChatMarkdown } from '../chat/chat-markdown';

// Line-level diff utilities for update drafts

// tslint:disable-next-line: no-magic-numbers
const GREY_600 = 600;

// Shared badge styles for update drafts (used by dialog and feed components)
export const updateDraftBadgeStyles = (theme: Theme) => css`
    .badge {
        font-size: 11px;
        font-weight: 500;
        padding: 2px 8px;
        border-radius: 12px;
        text-transform: uppercase;
    }

    .badge--applied {
        background: rgba(76, 175, 80, 0.1);
        color: ${theme.palette.success.dark};
    }

    .badge--dismissed {
        background: rgba(158, 158, 158, 0.1);
        color: ${theme.palette.grey[GREY_600]};
    }

    .badge--stale {
        background: rgba(255, 152, 0, 0.1);
        color: #e65100;
    }
`;

export const updateDiffDialogStyles = (theme: Theme) => css`
    ${updateDraftBadgeStyles(theme)}

    .title {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding-right: 8px;
    }

    .content {
        min-width: 700px;
        max-height: 70vh;
    }

    .explanation {
        margin-bottom: 16px;
    }

    .source {
        margin-bottom: 24px;
    }

    .section {
        margin-bottom: 24px;
    }

    .sectionHeader {
        font-size: 14px;
        font-weight: 600;
        color: ${theme.palette.text.secondary};
        margin: 24px 0 8px;
        text-transform: uppercase;
        letter-spacing: 0.5px;
    }

    .fieldBlock {
        margin-bottom: 16px;
        border: 1px solid ${theme.palette.divider};
        border-radius: 6px;
        overflow: hidden;
    }

    .fieldHeader {
        font-weight: 600;
        font-size: 13px;
        padding: 8px 12px;
        background: ${theme.palette.grey[100]};
        border-bottom: 1px solid ${theme.palette.divider};
    }

    .diffLines {
        font-size: 13px;
        line-height: 1.6;
    }

    .diffLine {
        display: flex;
        padding: 2px 12px;
    }

    .diffLine--same {
        background: transparent;
    }

    .diffLine--removed {
        background: #ffeef0;
    }

    .diffLine--added {
        background: #e6ffed;
    }

    .diffPrefix {
        flex-shrink: 0;
        width: 20px;
        font-family: 'SF Mono', 'Monaco', monospace;
        font-weight: 600;
        user-select: none;
    }

    .diffPrefix--removed {
        color: #cb2431;
    }

    .diffPrefix--added {
        color: #22863a;
    }

    .diffContent {
        flex: 1;
        min-width: 0;

        /* Compact markdown rendering */
        .agent__mdParagraph,
        .agent__mdHeading,
        .agent__mdList {
            margin: 0;
        }
    }
`;

export interface DiffLine {
    type: 'same' | 'added' | 'removed';
    text: string;
}

export interface DiffSection {
    title: string;
    oldValues: Record<string, unknown>;
    newValues: Record<string, unknown>;
    fieldLabels: Record<string, string>;
}

export const CLIENT_FIELD_LABELS: Record<string, string> = {
    description: 'Company Description',
    foundedDate: 'Founded Date',
    funding: 'Funding',
    hq: 'Headquarters',
    salesPitch: 'Sales Pitch',
    teamSize: 'Team Size',
    website: 'Website'
};

export const JOB_FIELD_LABELS: Record<string, string> = {
    compensation: 'Compensation',
    description: 'Job Description',
    diversity: 'Diversity',
    hiringManager: 'Hiring Manager',
    interviewPrep: 'Interview Prep',
    introSettings: 'Intro Settings',
    location: 'Location',
    maxExperienceYears: 'Max Experience (Years)',
    minExperienceYears: 'Min Experience (Years)',
    outreachAIContext: 'Outreach AI Context',
    scoringAIContext: 'Scoring AI Context',
    submissionGenAIContext: 'Submission Gen AI Context',
    submissionSettings: 'Submission Settings',
    visa: 'Visa Requirements'
};

const NESTED_FIELD_LABELS: Record<string, Record<string, string>> = {
    introSettings: {
        directions: 'Directions',
        emailCC: 'CC',
        emailTo: 'Email To',
        introEmailDisabled: 'Intro Email Disabled'
    },
    outreachAIContext: {
        directions: 'Directions',
        highlights: 'Highlights',
        keyCriteria: 'Key Criteria'
    },
    scoringAIContext: {
        briefSource: 'Brief Source',
        briefUpdatedAt: 'Brief Updated At',
        scoringBrief: 'Scoring Brief'
    },
    submissionGenAIContext: {
        directions: 'Directions'
    },
    submissionSettings: {
        atsLink: 'ATS Link',
        description: 'Description',
        emailCC: 'CC',
        emailTo: 'Email To',
        password: 'Password',
        type: 'Type',
        username: 'Username'
    }
};

export function formatValue(value: unknown): string {
    if (value === null || value === undefined) return '';
    if (Array.isArray(value)) return value.join(', ');
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
}

// Check if the current data differs from the stored "old" snapshot
export function checkStaleness(
    storedOld: Record<string, unknown>,
    currentData: Record<string, unknown> | null | undefined
): boolean {
    if (!currentData) return false;

    for (const key of Object.keys(storedOld)) {
        const storedValue = formatValue(storedOld[key]);
        const currentValue = formatValue(currentData[key]);
        if (storedValue !== currentValue) {
            return true;
        }
    }
    return false;
}

// Line-level diff using LCS (Longest Common Subsequence)
export function computeLineDiff(oldText: string, newText: string): DiffLine[] {
    const oldLines = oldText.split('\n');
    const newLines = newText.split('\n');

    const m = oldLines.length;
    const n = newLines.length;
    const dp: number[][] = Array(m + 1)
        .fill(null)
        .map(() => Array(n + 1).fill(0));

    for (let idx = 1; idx <= m; idx++) {
        for (let jdx = 1; jdx <= n; jdx++) {
            if (oldLines[idx - 1] === newLines[jdx - 1]) {
                dp[idx][jdx] = dp[idx - 1][jdx - 1] + 1;
            } else {
                dp[idx][jdx] = Math.max(dp[idx - 1][jdx], dp[idx][jdx - 1]);
            }
        }
    }

    // Backtrack
    const temp: DiffLine[] = [];
    let i = m;
    let j = n;

    while (i > 0 || j > 0) {
        if (i > 0 && j > 0 && oldLines[i - 1] === newLines[j - 1]) {
            temp.push({ text: oldLines[i - 1], type: 'same' });
            i--;
            j--;
        } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
            temp.push({ text: newLines[j - 1], type: 'added' });
            j--;
        } else {
            temp.push({ text: oldLines[i - 1], type: 'removed' });
            i--;
        }
    }

    return temp.reverse();
}

// Component to render a diff section with field blocks
export interface DiffSectionViewProps {
    section: DiffSection;
}

function renderFieldDiff(field: string, label: string, oldVal: unknown, newVal: unknown): React.ReactNode {
    const oldStr = formatValue(oldVal);
    const newStr = formatValue(newVal);

    if (oldStr === newStr) return null;

    const diffLines = computeLineDiff(oldStr, newStr);

    const lineElements = diffLines.map((line, lineIdx) => {
        const prefix = line.type === 'removed' ? '-' : line.type === 'added' ? '+' : ' ';
        const lineClass = `diffLine diffLine--${line.type}`;
        const prefixClass = `diffPrefix diffPrefix--${line.type}`;
        const content = line.text.trim() || ' ';
        return (
            <div key={lineIdx} className={lineClass}>
                <span className={prefixClass}>{prefix}</span>
                <div className="diffContent">
                    <ChatMarkdown>{content}</ChatMarkdown>
                </div>
            </div>
        );
    });

    return (
        <div key={field} className="fieldBlock">
            <div className="fieldHeader">{label}</div>
            <div className="diffLines">{lineElements}</div>
        </div>
    );
}

function renderNestedDiff(
    field: string,
    parentLabel: string,
    subLabels: Record<string, string>,
    oldObj: unknown,
    newObj: unknown
): React.ReactNode[] {
    const oldMap = (oldObj && typeof oldObj === 'object' ? oldObj : {}) as Record<string, unknown>;
    const newMap = (newObj && typeof newObj === 'object' ? newObj : {}) as Record<string, unknown>;
    const allKeys = [...new Set([...Object.keys(oldMap), ...Object.keys(newMap)])].sort();

    const blocks = allKeys
        .map((subKey) => {
            const label = `${parentLabel} › ${subLabels[subKey] || subKey}`;
            return renderFieldDiff(`${field}.${subKey}`, label, oldMap[subKey], newMap[subKey]);
        })
        .filter(Boolean);

    return blocks;
}

export const DiffSectionView: React.FC<DiffSectionViewProps> = ({ section }) => {
    const fields = Object.keys(section.newValues);
    if (fields.length === 0) return null;

    const fieldBlocks = fields
        .flatMap((field) => {
            const oldVal = section.oldValues?.[field];
            const newVal = section.newValues[field];
            const parentLabel = section.fieldLabels[field] || field;
            const subLabels = NESTED_FIELD_LABELS[field];

            if (subLabels && (typeof newVal === 'object' || typeof oldVal === 'object')) {
                return renderNestedDiff(field, parentLabel, subLabels, oldVal, newVal);
            }

            return [renderFieldDiff(field, parentLabel, oldVal, newVal)];
        })
        .filter(Boolean);

    if (fieldBlocks.length === 0) return null;

    return (
        <div className="section">
            <div className="sectionHeader">{section.title}</div>
            {fieldBlocks}
        </div>
    );
};
